import { useMemo } from 'react';
import { encodeQr } from '@filmsloth/shared';

interface QrCodeProps {
  /** What a scanner should end up with — here, always the invite link. */
  text: string;
  /** Read out instead of the image; a QR code is not self-describing. */
  label: string;
  className?: string;
}

/** How many light modules surround the code. Four is the specified minimum. */
const QUIET_ZONE = 4;

/**
 * A QR code as inline SVG.
 *
 * SVG rather than a canvas: it scales to whatever the layout gives it without
 * a device-pixel-ratio dance, it costs no request, and it survives the
 * screenshot somebody inevitably sends to a friend who is not in the room.
 *
 * Deliberately black on white even though the app is dark throughout. A
 * scanner wants contrast and the expected polarity; an inverted code is
 * readable by some phones and not others, and this is the one element whose
 * whole job is to be read by a machine at an angle across a table.
 */
export function QrCode({ text, label, className }: QrCodeProps) {
  const matrix = useMemo(() => {
    try {
      return encodeQr(text);
    } catch {
      // Too long for the versions the encoder covers. The invite link is
      // always shown as text as well, so the answer is to render nothing
      // rather than something a scanner would refuse.
      return null;
    }
  }, [text]);

  const path = useMemo(() => {
    if (!matrix) return '';
    const parts: string[] = [];
    for (let row = 0; row < matrix.size; row++) {
      let run = 0;
      for (let column = 0; column <= matrix.size; column++) {
        const dark = column < matrix.size && matrix.modules[row]![column];
        if (dark) {
          run++;
          continue;
        }
        if (run > 0) {
          // One rectangle per horizontal run rather than per module: about a
          // third of the path data, and identical output.
          parts.push(`M${column - run + QUIET_ZONE} ${row + QUIET_ZONE}h${run}v1h-${run}z`);
          run = 0;
        }
      }
    }
    return parts.join('');
  }, [matrix]);

  if (!matrix) return null;

  const side = matrix.size + QUIET_ZONE * 2;

  return (
    <svg
      className={className ? `qr ${className}` : 'qr'}
      viewBox={`0 0 ${side} ${side}`}
      role="img"
      aria-label={label}
      /* Without this the browser antialiases module edges at some zoom
         levels, which is exactly the blur a scanner trips over. */
      shapeRendering="crispEdges"
    >
      <rect width={side} height={side} fill="#ffffff" />
      <path d={path} fill="#000000" />
    </svg>
  );
}
